import { useTranslation } from '../../../shared/i18n';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Button, Card, Col, Descriptions, Divider, Empty, Form, Input, InputNumber, Modal, Row,
    Select, Space, Spin, Switch, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import { DeleteOutlined, EditOutlined, KeyOutlined, LockOutlined, ReloadOutlined, SafetyOutlined, SyncOutlined } from '@ant-design/icons';
import domainRegistrationService from '../../services/domainRegistrationService';
import RenewalTag from './components/manage/RenewalTag';
import registrarService from '../../services/registrarService';
import dnsService from '../../services/dnsService';
import PageHeader from '../../../shared/components/PageHeader';
import DomainStatusTag from '../../../shared/components/DomainStatusTag';
import { formatDate, formatDateTime, daysUntil } from '../../../shared/utils/date';
import { userPath } from '../../../shared/config/paths';
import RenewModal from './components/manage/RenewModal';
import NameserverEditor, { findZoneForDomain } from './components/manage/NameserverEditor';
import { showAuthCode } from './components/manage/authCode';
import DomainGifts from './components/manage/DomainGifts';
import DomainContacts from './components/manage/DomainContacts';
import TransferProgress from './components/manage/TransferProgress';

const { Text } = Typography;

const TABS = ['overview', 'nameservers', 'contacts', 'security', 'glue'];
const RENEWABLE = ['active', 'expired'];
const SYNCABLE = ['active', 'expired', 'redemption'];

const IPV4 = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IPV6 = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^(([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4})?::([0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}$/;

const ALGORITHMS = [
    { value: 13, label: '13 · ECDSA P-256' },
    { value: 14, label: '14 · ECDSA P-384' },
    { value: 15, label: '15 · Ed25519' },
    { value: 8, label: '8 · RSA/SHA-256' },
    { value: 7, label: '7 · RSASHA1-NSEC3' },
    { value: 5, label: '5 · RSA/SHA-1' },
];
const DIGESTS = [
    { value: 2, label: '2 · SHA-256' },
    { value: 4, label: '4 · SHA-384' },
    { value: 1, label: '1 · SHA-1' },
];

const OP_STATUS = {
    pending: { color: 'processing', text: '待处理' },
    processing: { color: 'processing', text: '处理中' },
    completed: { color: 'success', text: '完成' },
    success: { color: 'success', text: '完成' },
    failed: { color: 'error', text: '失败' },
};

const DomainDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'overview';
    const [tab, setTab] = useState(initialTab);

    const [loading, setLoading] = useState(true);
    const [domain, setDomain] = useState(null);
    const [zone, setZone] = useState(null);
    const [renewOpen, setRenewOpen] = useState(false);
    const [busy, setBusy] = useState('');

    const [fieldDefs, setFieldDefs] = useState([]);

    // dnssec
    const [dnssecLoaded, setDnssecLoaded] = useState(false);
    const [dnssecLoading, setDnssecLoading] = useState(false);
    const [zoneDnssec, setZoneDnssec] = useState(null);
    const [registrarDnssec, setRegistrarDnssec] = useState(null);
    const [dsSubmitting, setDsSubmitting] = useState(false);
    const [dsForm] = Form.useForm();

    // glue
    const [glue, setGlue] = useState([]);
    const [glueLoaded, setGlueLoaded] = useState(false);
    const [glueLoading, setGlueLoading] = useState(false);
    const [glueSubmitting, setGlueSubmitting] = useState(false);
    const [glueModal, setGlueModal] = useState(null); // {mode:'add'|'edit', record}
    const [glueForm] = Form.useForm();

    const load = async () => {
        setLoading(true);
        try {
            const r = await domainRegistrationService.getDomainDetail(id);
            if (!r.success) {
                message.error(r.error || t('Failed to load'));
                return;
            }
            const d = r.data;
            setDomain(d);
            if (d.tld && d.registrar_id) {
                registrarService.getAdditionalFields(d.tld, d.registrar_id)
                    .then((resp) => {
                        const raw = resp?.data;
                        setFieldDefs(Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []));
                    })
                    .catch(() => setFieldDefs([]));
            }
            try {
                const z = await dnsService.getDnsZones({ search: d.domain });
                setZone(findZoneForDomain(Array.isArray(z?.data) ? z.data : [], d));
            } catch (_) { setZone(null); }
        } catch (e) {
            message.error(e.error || e.message || t('Failed to load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        setDnssecLoaded(false);
        setGlueLoaded(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const changeTab = (key) => {
        setTab(key);
        const p = new URLSearchParams(searchParams);
        if (key === 'overview') p.delete('tab'); else p.set('tab', key);
        setSearchParams(p, { replace: true });
    };

    // lazy-load tab data
    useEffect(() => {
        if (!domain) return;
        if (tab === 'nameservers' && domain.supports_dnssec && !dnssecLoaded) loadDnssec();
        if (tab === 'glue' && !glueLoaded) loadGlue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, domain?.id, zone?.id]);

    const active = domain?.status === 'active';
    const name = domain?.domain_unicode || domain?.domain || '';

    // ---------- actions ----------
    const run = async (key, fn, ok) => {
        setBusy(key);
        try {
            const r = await fn();
            if (r?.success) {
                if (ok) message.success(ok(r));
                await load();
            } else {
                message.error(r?.error || t('Operation failed'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Operation failed'));
        } finally {
            setBusy('');
        }
    };

    const toggleLock = () => run('lock', () => domainRegistrationService.toggleDomainLock({ id: domain.id, lock: !domain.domain_lock }),
        () => (domain.domain_lock ? t('Unlocked') : t('Locked')));
    const togglePrivacy = () => run('privacy', () => domainRegistrationService.toggleWhoisPrivacy({ id: domain.id, enable: !domain.whois_privacy }),
        () => (domain.whois_privacy ? t('Privacy protection disabled') : t('Privacy protection enabled')));
    // 隐私模式：开 = 补附加费账单（付款后生效）；关 = 立即把注册人改回自己的资料
    const togglePrivacyMode = (on) => {
        if (!on) {
            Modal.confirm({
                title: t('Turn off privacy mode'),
                content: t('The registrar will list your own contact details'),
                okText: t('Close'),
                cancelText: t('Cancel'),
                onOk: () => run('privacyMode', () => domainRegistrationService.setPrivacyMode({ id: domain.id, enable: false }), () => t('Privacy mode is off')),
            });
            return;
        }
        run('privacyMode', async () => {
            const r = await domainRegistrationService.setPrivacyMode({ id: domain.id, enable: true });
            if (r?.success && r.data?.invoice_id) navigate(userPath(`/billing/invoices/${r.data.invoice_id}`));
            return r;
        }, (r) => (r.data?.invoice_id ? t('Please complete payment; it takes effect automatically after payment') : t('Privacy mode is on')));
    };
    const toggleAutoRenew = (v) => run('auto', () => domainRegistrationService.toggleAutoRenew({ id: domain.id, auto_renew: v }),
        () => (v ? t('Auto-renewal enabled') : t('Auto-renewal disabled')));

    const syncNow = () => run('sync', () => domainRegistrationService.triggerSync(domain.id),
        (r) => ((r.data?.changed || []).length ? t('Synced, information updated') : t('Synced, no changes')));

    const restore = () => {
        Modal.confirm({
            title: t('Restore {{name}}', { name: name }),
            content: domain.restore_price ? t('The redemption fee of {{restore_price}} will be deducted from your balance', { restore_price: domain.restore_price }) : t('The redemption fee will be deducted from your balance'),
            okText: t('Confirm restore'),
            onOk: () => run('restore', () => domainRegistrationService.restoreDomain(domain.id), () => t('Restore request submitted')),
        });
    };

    // ---------- dnssec ----------
    const loadDnssec = async () => {
        setDnssecLoading(true);
        try {
            if (zone?.id) {
                const s = await dnsService.getDnssecStatus(zone.id);
                setZoneDnssec(s?.success === false ? null : (s?.data || s || null));
            } else {
                setZoneDnssec(null);
            }
            try {
                const r = await domainRegistrationService.getRegistrarDnssec(domain.id);
                setRegistrarDnssec(r?.data || r || null);
            } catch (_) { setRegistrarDnssec(null); }
        } finally {
            setDnssecLoading(false);
            setDnssecLoaded(true);
        }
    };

    const submitDs = async (payload, okText) => {
        setDsSubmitting(true);
        try {
            const r = await domainRegistrationService.submitDnssecToRegistrar({ domain_id: domain.id, ...payload });
            if (r.success) {
                message.success(okText);
                dsForm.resetFields();
                loadDnssec();
            } else {
                message.error(r.error || t('Submission failed'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Submission failed'));
        } finally {
            setDsSubmitting(false);
        }
    };

    const deleteDs = () => {
        Modal.confirm({
            title: t('Delete DS record?'),
            content: t('DNSSEC validation will stop working after deletion'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const r = await domainRegistrationService.deleteRegistrarDnssec({ domain_id: domain.id });
                    if (r.success) { message.success(t('Deleted')); loadDnssec(); } else message.error(r.error || t('Deletion failed'));
                } catch (e) {
                    message.error(e.error || e.message || t('Deletion failed'));
                }
            },
        });
    };

    // ---------- glue ----------
    const loadGlue = async () => {
        setGlueLoading(true);
        try {
            const r = await domainRegistrationService.getGlueRecords(domain.id);
            const list = r?.data?.glue_records ?? r?.data;
            setGlue(Array.isArray(list) ? list : []);
        } catch (_) {
            setGlue([]);
        } finally {
            setGlueLoading(false);
            setGlueLoaded(true);
        }
    };

    const openGlue = (record) => {
        setGlueModal({ mode: record ? 'edit' : 'add', record });
    };

    const saveGlue = async () => {
        const v = await glueForm.validateFields();
        setGlueSubmitting(true);
        try {
            const body = { domain_id: domain.id, hostname: glueModal.mode === 'edit' ? glueModal.record.hostname : v.hostname, ipv4: v.ipv4, ipv6: v.ipv6 || null };
            const r = glueModal.mode === 'edit'
                ? await domainRegistrationService.updateGlueRecord(body)
                : await domainRegistrationService.addGlueRecord(body);
            if (r.success) {
                message.success(t('Saved'));
                setGlueModal(null);
                loadGlue();
            } else {
                message.error(r.error || t('Failed to save'));
            }
        } catch (e) {
            message.error(e.error || e.message || t('Failed to save'));
        } finally {
            setGlueSubmitting(false);
        }
    };

    const deleteGlue = (hostname) => {
        Modal.confirm({
            title: t('Delete {{hostname}}?', { hostname: hostname }),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const r = await domainRegistrationService.deleteGlueRecord({ domain_id: domain.id, hostname });
                    if (r.success) { message.success(t('Deleted')); loadGlue(); } else message.error(r.error || t('Deletion failed'));
                } catch (e) {
                    message.error(e.error || e.message || t('Deletion failed'));
                }
            },
        });
    };

    // ---------- render helpers ----------
    const days = domain?.expires_at ? daysUntil(domain.expires_at) : null;
    const inRedemption = domain && (domain.status === 'redemption'
        || (domain.redemption_period_ends_at && daysUntil(domain.redemption_period_ends_at) > 0 && domain.status !== 'active'));
    const nameservers = Array.isArray(domain?.nameservers) ? domain.nameservers : [];
    const additional = domain?.additional_info || {};
    const additionalRows = (() => {
        const rows = [];
        fieldDefs.forEach((def) => {
            const v = additional[def.name] ?? additional[def.contact_field_mapping];
            if (v !== undefined && v !== null && v !== '') rows.push({ key: def.name, label: def.label || def.name, value: String(v) });
        });
        if (rows.length === 0) {
            Object.entries(additional).forEach(([k, v]) => {
                if (v !== null && v !== '' && typeof v !== 'object') rows.push({ key: k, label: k, value: String(v) });
            });
        }
        return rows;
    })();

    const overview = domain && (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small" bordered>
                <Descriptions.Item label={t('Status')}><DomainStatusTag status={domain.status} /></Descriptions.Item>
                <Descriptions.Item label={t('Registration date')}>{formatDate(domain.registered_at || domain.created_at)}</Descriptions.Item>
                <Descriptions.Item label={t('Expires at')}>
                    <span style={{ color: days !== null && days <= 30 ? (days < 0 ? '#ff4d4f' : '#fa8c16') : undefined, fontWeight: days !== null && days <= 30 ? 600 : undefined }}>
                        {formatDate(domain.expires_at)}
                    </span>
                    {days !== null && days >= 0 && days <= 30 && <Text type="secondary" style={{ marginLeft: 6 }}>{t('In {{days}} days', { days })}</Text>}
                </Descriptions.Item>
                <Descriptions.Item label={<Tooltip title={t('Automatically renew using your account balance before expiration')}>{t('Auto-renewal')}</Tooltip>}>
                    <Switch size="small" checked={!!domain.auto_renew} loading={busy === 'auto'} disabled={!RENEWABLE.includes(domain.status)} onChange={toggleAutoRenew} />
                </Descriptions.Item>
                <Descriptions.Item label={t('DNS servers')}>
                    {nameservers.length ? nameservers.join(', ') : <Text type="secondary">{t('Not set')}</Text>}
                </Descriptions.Item>
                <Descriptions.Item label={t('DNS resolution')}>
                    {zone ? <Link to={userPath(`/dns/records/${zone.id}`)}>{t('Manage DNS records')}</Link> : <Link to={userPath('/dns/zones')}>{t('Add DNS record')}</Link>}
                </Descriptions.Item>
                <Descriptions.Item label={t('Domain lock')}>{domain.domain_lock ? <Tag icon={<LockOutlined />} color="blue">{t('Locked')}</Tag> : <Tag>{t('Unlocked')}</Tag>}</Descriptions.Item>
                {domain.privacy?.enabled && (
                    <Descriptions.Item label={t('Privacy mode')}><Tooltip title={t('The domain is registered in our name; your details stay private')}><Tag icon={<SafetyOutlined />} color="purple">{t('Enabled')}</Tag></Tooltip></Descriptions.Item>
                )}
                <Descriptions.Item label={t('Privacy protection')}>
                    {domain.supports_privacy === false ? <Text type="secondary">{t('Not supported')}</Text> : (domain.whois_privacy ? <Tag icon={<SafetyOutlined />} color="green">{t('Enabled')}</Tag> : <Tag>{t('Disabled')}</Tag>)}
                </Descriptions.Item>
                {domain.domain_unicode && domain.domain_unicode !== domain.domain && (
                    <Descriptions.Item label="Punycode"><Text code>{domain.domain}</Text></Descriptions.Item>
                )}
                {additionalRows.map((r) => (
                    <Descriptions.Item key={r.key} label={t(r.label)}>{r.value}</Descriptions.Item>
                ))}
            </Descriptions>
            <DomainGifts domainId={domain.id} />
            {(domain.operations || []).length > 0 && (
                <Card size="small" title={t('Recent actions')}>
                    <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: true }}
                        dataSource={domain.operations}
                        columns={[
                            { title: t('Time'), dataIndex: 'created_at', width: 150, render: (v) => formatDateTime(v) },
                            { title: t('Actions'), dataIndex: 'operation_type' },
                            { title: t('Status'), dataIndex: 'status', width: 90, render: (s) => { const c = OP_STATUS[s] || { color: 'default', text: s }; return <Tag color={c.color}>{t(c.text)}</Tag>; } },
                            { title: t('Notes'), dataIndex: 'error_message', render: (m) => m || '-' },
                        ]}
                    />
                </Card>
            )}
        </Space>
    );

    const dsRecords = registrarDnssec?.dnssec_data || [];
    const nsTab = domain && (
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
            <div style={{ maxWidth: 560 }}>
                {active ? (
                    <NameserverEditor domain={domain} zone={zone} onSaved={() => load()} />
                ) : (
                    <Text>{nameservers.join(', ') || t('Not set')}</Text>
                )}
            </div>
            {domain.supports_dnssec && (
                <Card
                    size="small"
                    title="DNSSEC"
                    extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={loadDnssec} />}
                >
                    <Spin spinning={dnssecLoading}>
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            {dsRecords.length > 0 ? (
                                <>
                                    {dsRecords.map((ds, i) => (
                                        <Text key={i} code copyable style={{ wordBreak: 'break-all' }}>
                                            {`${ds.key_tag} ${ds.algorithm} ${ds.digest_type} ${ds.digest}`}
                                        </Text>
                                    ))}
                                    <Button danger size="small" icon={<DeleteOutlined />} onClick={deleteDs} style={{ width: 'fit-content' }}>{t('Delete DS record')}</Button>
                                </>
                            ) : (
                                <Text type="secondary">{t('No DS records configured')}</Text>
                            )}
                            {zone && zoneDnssec?.enabled && (
                                <Button type="primary" icon={<SyncOutlined />} loading={dsSubmitting} style={{ width: 'fit-content' }}
                                    onClick={() => submitDs({ zone_id: zone.id }, t('DS records synced'))}>
                                    {t('Sync DS from Yeap DNS')}
                                </Button>
                            )}
                            {zone && zoneDnssec && !zoneDnssec.enabled && (
                                <Text type="secondary">
                                    {t('DNSSEC disabled on Yeap DNS ·')} <Link to={userPath(`/dns/records/${zone.id}`)}>{t('Enable now')}</Link>
                                </Text>
                            )}
                            {dsRecords.length < 4 && (
                                <>
                                    <Divider plain orientation="left" style={{ margin: '4px 0' }}>{t('Manually add DS')}</Divider>
                                    <Form form={dsForm} layout="vertical" onFinish={(v) => submitDs(v, t('DS records submitted'))}>
                                        <Row gutter={12}>
                                            <Col xs={24} sm={6}>
                                                <Form.Item name="key_tag" label="Key Tag" rules={[{ required: true, message: t('Required') }]}>
                                                    <InputNumber min={0} max={65535} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12} sm={9}>
                                                <Form.Item name="algorithm" label={t('Algorithm')} rules={[{ required: true, message: t('Required') }]}>
                                                    <Select options={ALGORITHMS} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={12} sm={9}>
                                                <Form.Item name="digest_type" label={t('Digest type')} rules={[{ required: true, message: t('Required') }]}>
                                                    <Select options={DIGESTS} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={24}>
                                                <Form.Item name="digest" label={t('Digest')} rules={[{ required: true, message: t('Required') }, { pattern: /^[A-Fa-f0-9]+$/, message: t('Hexadecimal') }]}>
                                                    <Input.TextArea rows={2} style={{ fontFamily: 'monospace' }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Button htmlType="submit" loading={dsSubmitting}>{t('Submit')}</Button>
                                    </Form>
                                </>
                            )}
                        </Space>
                    </Spin>
                </Card>
            )}
        </Space>
    );

    const contactsTab = domain && <DomainContacts domain={domain} />;

    const securityRow = (title, tip, control) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid rgba(128,128,128,0.15)', gap: 12 }}>
            <div>
                <div style={{ fontWeight: 500 }}>{title}</div>
                <Text type="secondary" style={{ fontSize: 12 }}>{tip}</Text>
            </div>
            {control}
        </div>
    );

    const securityTab = domain && (
        <div style={{ maxWidth: 640 }}>
            {securityRow(t('Domain lock'), t('Transfers out are disabled while locked'), (
                <Switch checked={!!domain.domain_lock} loading={busy === 'lock'} disabled={!active} onChange={toggleLock} />
            ))}
            {!domain.privacy?.enabled && securityRow(t('Privacy protection'), domain.supports_privacy === false ? t('This TLD is not supported') : t('Hide contact details in WHOIS'), (
                <Switch checked={!!domain.whois_privacy} loading={busy === 'privacy'} disabled={!active || domain.supports_privacy === false} onChange={togglePrivacy} />
            ))}
            {(domain.privacy?.supported || domain.privacy?.enabled) && securityRow(
                <Space size={6}>{t('Privacy mode')}{domain.privacy?.enabled && <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t('Enabled')}</Tag>}</Space>,
                domain.privacy?.enabled
                    ? <>{t('The domain is registered in our name; your details stay private')}{domain.privacy?.email && <><br /><Text type="secondary" copyable={{ text: domain.privacy.email }} style={{ fontSize: 12 }}>{domain.privacy.email}</Text></>}</>
                    : (domain.privacy?.enable_price
                        ? t('Turning it on costs {{amount}} {{currency}} (remaining term); renewal {{renewal}} {{currency}}/year', { amount: domain.privacy.enable_price.amount, currency: domain.privacy.enable_price.currency, renewal: domain.privacy.renewal_price })
                        : t('The domain is registered in our name; your details stay private')),
                <Switch checked={!!domain.privacy?.enabled} loading={busy === 'privacyMode'} disabled={!active} onChange={togglePrivacyMode} />
            )}
            {securityRow(t('Transfer code'), domain.privacy?.enabled ? t('Turn off privacy mode before transferring out') : (domain.domain_lock ? t('Unlock the domain before retrieving') : t('Used when transferring to another provider')), (
                <Button icon={<KeyOutlined />} disabled={!active || !!domain.domain_lock || !!domain.privacy?.enabled} onClick={() => showAuthCode(domain)}>{t('Get')}</Button>
            ))}
        </div>
    );

    const glueTab = domain && (
        <Spin spinning={glueLoading}>
            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" disabled={!active} onClick={() => openGlue(null)}>{t('Add')}</Button>
                <Button icon={<ReloadOutlined />} onClick={loadGlue} />
                <Text type="secondary">{t('E.g. ns1.{{domain}}', { domain: domain.domain })}</Text>
            </Space>
            <Table
                size="small"
                rowKey="hostname"
                pagination={false}
                dataSource={glue}
                scroll={{ x: 520 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No subdomain nameservers yet')} /> }}
                columns={[
                    { title: t('Hostname'), dataIndex: 'hostname', render: (t) => <Text code>{t}</Text> },
                    { title: 'IPv4', dataIndex: 'ipv4', render: (t) => t || '-' },
                    { title: 'IPv6', dataIndex: 'ipv6', render: (t) => t || '-' },
                    {
                        title: '', key: 'a', width: 90, render: (_, r) => (
                            <Space size={0}>
                                <Button size="small" type="text" icon={<EditOutlined />} disabled={!active} onClick={() => openGlue(r)} />
                                <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={!active} onClick={() => deleteGlue(r.hostname)} />
                            </Space>
                        ),
                    },
                ]}
            />
        </Spin>
    );

    const domainSuffixRe = domain ? new RegExp(`^[a-zA-Z0-9-]+\\.${domain.domain.replace(/\./g, '\\.')}$`, 'i') : null;

    return (
        <Spin spinning={loading && !domain}>
            <PageHeader
                title={name || t('Domain details')}
                onBack={() => navigate(userPath('/domain-registration/my-domains'))}
                tags={domain ? <><DomainStatusTag status={domain.status} /><RenewalTag renewal={domain.renewal} /></> : null}
                extra={domain && (
                    <>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                        {SYNCABLE.includes(domain.status) && (
                            <Tooltip title={domain.last_sync_at ? t('Check expiry date and status with the registrar · Last checked {{date}}', { date: formatDateTime(domain.last_sync_at) }) : t('Check expiry date and status with the registrar')}>
                                <Button icon={<SyncOutlined />} loading={busy === 'sync'} onClick={syncNow}>{t('Sync')}</Button>
                            </Tooltip>
                        )}
                        {inRedemption && <Button danger loading={busy === 'restore'} onClick={restore}>{t('Restore')}</Button>}
                        {RENEWABLE.includes(domain.status) && <Button type="primary" onClick={() => setRenewOpen(true)}>{t('Renew')}</Button>}
                    </>
                )}
            />

            {domain && days !== null && days <= 30 && RENEWABLE.includes(domain.status) && (
                <Alert
                    type={days < 0 ? 'error' : 'warning'}
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={days < 0 ? t('Your domain has expired. Please renew it as soon as possible.') : t('Expires in {{days}} days', { days: days })}
                    action={<Button size="small" type="primary" onClick={() => setRenewOpen(true)}>{t('Renew')}</Button>}
                />
            )}

            {domain && <TransferProgress domain={domain} onChange={load} />}

            {domain && (
                <Card bordered={false}>
                    <Tabs
                        activeKey={tab}
                        onChange={changeTab}
                        items={[
                            { key: 'overview', label: t('Overview'), children: overview },
                            { key: 'nameservers', label: t('DNS servers'), children: nsTab },
                            { key: 'contacts', label: t('Contacts'), children: contactsTab },
                            { key: 'security', label: t('Security'), children: securityTab },
                            { key: 'glue', label: t('Child nameservers'), children: glueTab, disabled: domain.supports_glue === false },
                        ]}
                    />
                </Card>
            )}

            <RenewModal open={renewOpen} domains={domain ? [domain] : []} onClose={() => setRenewOpen(false)} onDone={load} />

            <Modal
                title={glueModal?.mode === 'edit' ? t('Edit child nameserver') : t('Add child nameserver')}
                open={!!glueModal}
                onCancel={() => setGlueModal(null)}
                onOk={saveGlue}
                confirmLoading={glueSubmitting}
                destroyOnClose
            >
                <Form
                    form={glueForm}
                    layout="vertical"
                    preserve={false}
                    initialValues={glueModal?.record ? { hostname: glueModal.record.hostname, ipv4: glueModal.record.ipv4, ipv6: glueModal.record.ipv6 || '' } : {}}
                >
                    <Form.Item
                        name="hostname"
                        label={t('Hostname')}
                        rules={[{ required: true, message: t('Required') }, ...(domainSuffixRe ? [{ pattern: domainSuffixRe, message: t('Must end with .{{domain}}', { domain: domain?.domain }) }] : [])]}
                    >
                        <Input placeholder={`ns1.${domain?.domain || 'example.com'}`} disabled={glueModal?.mode === 'edit'} />
                    </Form.Item>
                    <Form.Item name="ipv4" label="IPv4" rules={[{ required: true, message: t('Required') }, { pattern: IPV4, message: t('Invalid IPv4 format') }]}>
                        <Input placeholder="192.0.2.1" />
                    </Form.Item>
                    <Form.Item name="ipv6" label={t('IPv6 (optional)')} rules={[{ pattern: IPV6, message: t('Invalid IPv6 format') }]}>
                        <Input placeholder="2001:db8::1" />
                    </Form.Item>
                </Form>
            </Modal>
        </Spin>
    );
};

export default DomainDetail;
